import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { inventoryAPI } from '../services/api';
import syncManager from '../services/syncManager';
import { Plus, Search, Package, Edit, Trash2, AlertTriangle, Wifi, WifiOff, RefreshCw, AlertCircle, CheckCircle, Clock, Wrench } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useOffline } from '../context/OfflineContext';

const Inventory = () => {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleteCategoryConfirmOpen, setDeleteCategoryConfirmOpen] = useState(false);
  const [editCategoryDialogOpen, setEditCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [customCategories, setCustomCategories] = useState([]);
  const [isOnline, setIsOnline] = useState(syncManager.checkOnlineStatus());
  const [syncQueue, setSyncQueue] = useState(syncManager.getStatus().pendingOperations);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { isOffline, checkOfflineAction } = useOffline();

  const [formData, setFormData] = useState({
    name: '',
    category: 'Furniture',
    quantity: '',
    available: '',
    condition: 'good',
    lastMaintenance: '',
    notes: ''
  });

  useEffect(() => {
    loadInventory();
    loadCustomCategories();
    
    // Listen for online/offline changes
    syncManager.addListener((status) => {
      if (status === 'online') {
        setIsOnline(true);
        toast({ title: '🌐 Online', description: 'Syncing changes...' });
        loadInventory(); // Reload from backend
        loadCustomCategories();
      } else if (status === 'offline') {
        setIsOnline(false);
        toast({ title: '📴 Offline', description: 'Changes will sync when online', variant: 'destructive' });
      } else if (status === 'sync-complete') {
        setSyncQueue(syncManager.getStatus().pendingOperations);
        toast({ title: '✅ Synced', description: 'All changes synced successfully!' });
      }
    });
  }, []);

  const loadCustomCategories = async () => {
    if (isOnline) {
      try {
        // ONLINE: Load from backend
        const categories = await inventoryAPI.getCategories();
        setCustomCategories(categories);
        // Cache in localStorage
        localStorage.setItem('customCategories', JSON.stringify(categories));
      } catch (error) {
        console.error('Failed to load categories from backend:', error);
        // Fallback to localStorage
        const stored = localStorage.getItem('customCategories');
        if (stored) {
          setCustomCategories(JSON.parse(stored));
        }
      }
    } else {
      // OFFLINE: Load from localStorage
      const stored = localStorage.getItem('customCategories');
      if (stored) {
        try {
          setCustomCategories(JSON.parse(stored));
        } catch (error) {
          console.error('Failed to load custom categories:', error);
        }
      }
    }
  };

  const saveCustomCategories = (categories) => {
    localStorage.setItem('customCategories', JSON.stringify(categories));
    setCustomCategories(categories);
  };

  useEffect(() => {
    filterInventory();
  }, [inventory, searchQuery, categoryFilter]);

  const loadInventory = async () => {
    try {
      const data = await inventoryAPI.getAll();
      setInventory(data || []);
      // Save to localStorage as cache for offline use
      localStorage.setItem('inventoryCache', JSON.stringify(data || []));
    } catch (error) {
      console.error('Failed to load inventory:', error);
      // Fallback to cache if offline
      const cached = localStorage.getItem('inventoryCache');
      if (cached) {
        setInventory(JSON.parse(cached));
      } else {
        setInventory([]);
      }
      toast({
        title: 'Error',
        description: 'Failed to load inventory. Showing cached data.',
        variant: 'destructive'
      });
    }
  };

  const filterInventory = () => {
    let filtered = [...inventory];

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(i => i.category === categoryFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter(i => 
        i.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        i.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredInventory(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) return;

    const totalQty = Number(formData.quantity);
    const availableQty = Number(formData.available);

    // Validation: Available quantity cannot exceed total quantity
    if (availableQty > totalQty) {
      toast({ 
        title: '❌ Validation Error', 
        description: 'Available quantity cannot be more than total quantity!', 
        variant: 'destructive' 
      });
      return;
    }

    // Validation: Quantities must be non-negative
    if (totalQty < 0 || availableQty < 0) {
      toast({ 
        title: '❌ Validation Error', 
        description: 'Quantities cannot be negative!', 
        variant: 'destructive' 
      });
      return;
    }

    setIsSubmitting(true);

    const inventoryData = {
      ...formData,
      quantity: totalQty,
      available: availableQty
    };

    try {
      if (isOnline) {
        // ONLINE: Sync to backend immediately
        if (editingItem) {
          await inventoryAPI.update(editingItem.id, inventoryData);
          toast({ title: '✅ Success', description: 'Item updated and synced!' });
        } else {
          await inventoryAPI.create(inventoryData);
          toast({ title: '✅ Success', description: 'Item added and synced!' });
        }
        await loadInventory(); // Reload from backend
      } else {
        // OFFLINE: Save locally and queue for sync
        handleOfflineSubmit(inventoryData);
        toast({ title: '📴 Offline', description: 'Saved locally. Will sync when online.' });
      }
      resetForm();
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to sync:', error);
      toast({ title: 'Error', description: 'Failed to sync. Saved locally.', variant: 'destructive' });
      // Save locally as fallback
      handleOfflineSubmit(inventoryData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOfflineSubmit = (inventoryData) => {
    const dataWithId = {
      id: editingItem ? editingItem.id : `INV_${Date.now()}`,
      ...inventoryData
    };

    if (editingItem) {
      const updated = inventory.map(i => i.id === editingItem.id ? dataWithId : i);
      localStorage.setItem('inventoryCache', JSON.stringify(updated));
      setInventory(updated);
      
      // Queue for sync
      syncManager.queueOperation({
        type: 'UPDATE',
        entity: 'inventory',
        endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/inventory/${editingItem.id}`,
        method: 'PUT',
        data: inventoryData
      });
    } else {
      const updated = [...inventory, dataWithId];
      localStorage.setItem('inventoryCache', JSON.stringify(updated));
      setInventory(updated);
      
      // Queue for sync
      syncManager.queueOperation({
        type: 'CREATE',
        entity: 'inventory',
        endpoint: `${process.env.REACT_APP_BACKEND_URL}/api/inventory/`,
        method: 'POST',
        data: inventoryData
      });
    }
    
    setSyncQueue(syncManager.getStatus().pendingOperations);
  };

  const handleEdit = (item) => {
    // Block edit when offline
    if (!checkOfflineAction('edit')) return;
    
    setEditingItem(item);
    setFormData(item);
    setDialogOpen(true);
  };

  const handleDeleteClick = (item) => {
    // Block delete when offline
    if (!checkOfflineAction('delete')) return;
    
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (itemToDelete) {
      try {
        if (isOnline) {
          await inventoryAPI.delete(itemToDelete.id);
          await loadInventory(); // Reload from backend
        } else {
          const updatedInventory = inventory.filter(i => i.id !== itemToDelete.id);
          setInventory(updatedInventory);
          localStorage.setItem('inventoryCache', JSON.stringify(updatedInventory));
        }
        toast({ title: 'Success', description: 'Item deleted successfully' });
      } catch (error) {
        console.error('Delete failed:', error);
        toast({ 
          title: 'Error', 
          description: 'Failed to delete item',
          variant: 'destructive'
        });
      } finally {
        // Always close dialog and reset state
        setDeleteConfirmOpen(false);
        setItemToDelete(null);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'Furniture',
      quantity: '',
      available: '',
      condition: 'good',
      lastMaintenance: '',
      notes: ''
    });
    setEditingItem(null);
    setIsSubmitting(false);
  };

  const handleAddCategory = () => {
    const categoryName = newCategoryName.trim();
    
    if (!categoryName) {
      toast({ 
        title: 'Error', 
        description: 'Please enter a category name',
        variant: 'destructive'
      });
      return;
    }

    // Check if category already exists
    if (categories.includes(categoryName)) {
      toast({ 
        title: 'Error', 
        description: 'Category already exists',
        variant: 'destructive'
      });
      return;
    }

    // Add to custom categories and save to localStorage
    const updatedCategories = [...customCategories, categoryName];
    saveCustomCategories(updatedCategories);

    toast({ 
      title: 'Success', 
      description: `Category "${categoryName}" added successfully! You can now use it when adding items.`
    });
    
    setNewCategoryName('');
    setCategoryDialogOpen(false);
  };

  const handleDeleteCategoryClick = (category) => {
    // Check if category has items
    const itemsInCategory = inventory.filter(i => i.category === category);
    if (itemsInCategory.length > 0) {
      toast({ 
        title: 'Cannot Delete', 
        description: `Category "${category}" has ${itemsInCategory.length} item(s). Please remove or reassign items first.`,
        variant: 'destructive'
      });
      return;
    }

    setCategoryToDelete(category);
    setDeleteCategoryConfirmOpen(true);
  };

  const handleDeleteCategoryConfirm = () => {
    // Remove from custom categories
    const updatedCategories = customCategories.filter(cat => cat !== categoryToDelete);
    saveCustomCategories(updatedCategories);

    toast({ 
      title: 'Success', 
      description: `Category "${categoryToDelete}" deleted successfully`
    });
    
    setDeleteCategoryConfirmOpen(false);
    setCategoryToDelete(null);
  };

  const handleEditCategoryClick = (category) => {
    setEditingCategory(category);
    setEditCategoryName(category);
    setEditCategoryDialogOpen(true);
  };

  const handleUpdateCategory = async () => {
    const newName = editCategoryName.trim();
    
    if (!newName) {
      toast({
        title: 'Error',
        description: 'Please enter a category name',
        variant: 'destructive'
      });
      return;
    }

    if (newName === editingCategory) {
      setEditCategoryDialogOpen(false);
      return;
    }

    // Check if new name already exists
    if (categories.includes(newName)) {
      toast({
        title: 'Error',
        description: 'A category with this name already exists',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Update in backend
      await inventoryAPI.updateCategory(editingCategory, newName);
      
      // Update local state
      const updatedCategories = customCategories.map(cat => 
        cat === editingCategory ? newName : cat
      );
      setCustomCategories(updatedCategories);
      localStorage.setItem('customCategories', JSON.stringify(updatedCategories));
      
      // Reload inventory to reflect category changes on items
      await loadInventory();
      
      toast({ 
        title: 'Success', 
        description: `Category renamed from "${editingCategory}" to "${newName}"`
      });
      
      setEditCategoryDialogOpen(false);
      setEditingCategory(null);
      setEditCategoryName('');
    } catch (error) {
      console.error('Failed to update category:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update category',
        variant: 'destructive'
      });
    }
  };

  const getConditionColor = (condition) => {
    const colors = {
      excellent: 'bg-green-100 dark:bg-green-900/30 text-green-800 border-green-200',
      good: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 border-blue-200',
      fair: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 border-orange-200',
      poor: 'bg-red-100 dark:bg-red-900/30 text-red-800 border-red-200'
    };
    return colors[condition] || colors.good;
  };

  const getAvailabilityPercentage = (item) => {
    return Math.round((item.available / item.quantity) * 100);
  };

  // Combine categories from inventory items and custom categories
  const inventoryCategories = [...new Set(inventory.map(i => i.category))];
  const categories = [...new Set([...inventoryCategories, ...customCategories])].sort();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-foreground">Inventory Management</h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-1">Track venue equipment and resources</p>
        </div>
        <div className="flex gap-2">
          {/* Add Category Dialog */}
          <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-purple-500 text-purple-600 hover:bg-purple-50 dark:bg-purple-900/20">
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent className="w-full sm:max-w-md max-h-[85vh] overflow-y-auto p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle>Add New Category</DialogTitle>
                <DialogDescription>
                  Create a new category for organizing inventory items
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="newCategory">Category Name *</Label>
                  <Input 
                    id="newCategory" 
                    placeholder="e.g., Tables, Chairs, Lighting"
                    value={newCategoryName} 
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => { setCategoryDialogOpen(false); setNewCategoryName(''); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddCategory} className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700">
                    Add Category
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Item Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 shadow-md"
                onClick={(e) => {
                  if (!checkOfflineAction('create')) {
                    e.preventDefault();
                  }
                }}
                disabled={isOffline}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </DialogTrigger>
            <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
              <DialogDescription>
                {editingItem ? 'Update inventory item details' : 'Add a new item to inventory'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Item Name *</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.length > 0 ? (
                        categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="Furniture">Furniture</SelectItem>
                          <SelectItem value="Electronics">Electronics</SelectItem>
                          <SelectItem value="Lighting">Lighting</SelectItem>
                          <SelectItem value="Decor">Decor</SelectItem>
                          <SelectItem value="Audio/Video">Audio/Video</SelectItem>
                          <SelectItem value="Kitchen">Kitchen</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="condition">Condition *</Label>
                  <Select value={formData.condition} onValueChange={(value) => setFormData({...formData, condition: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">Total Quantity *</Label>
                  <Input 
                    id="quantity" 
                    type="number" 
                    min="0"
                    value={formData.quantity} 
                    onChange={(e) => setFormData({...formData, quantity: e.target.value})} 
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="available">Available *</Label>
                  <Input 
                    id="available" 
                    type="number" 
                    min="0"
                    max={formData.quantity || undefined}
                    value={formData.available} 
                    onChange={(e) => setFormData({...formData, available: e.target.value})} 
                    required 
                    className={Number(formData.available) > Number(formData.quantity) ? 'border-red-500 focus:ring-red-500' : ''}
                  />
                  {Number(formData.available) > Number(formData.quantity) && formData.quantity && formData.available && (
                    <p className="text-xs text-red-500 mt-1">⚠️ Available cannot exceed total quantity ({formData.quantity})</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastMaintenance">Last Maintenance</Label>
                <Input id="lastMaintenance" type="date" value={formData.lastMaintenance} onChange={(e) => setFormData({...formData, lastMaintenance: e.target.value})} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input id="notes" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      {editingItem ? 'Updating...' : 'Adding...'}
                    </>
                  ) : (
                    <>{editingItem ? 'Update' : 'Add'} Item</>
                  )}
                </Button>
              </div>
            </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-muted-foreground">Total Items</p>
                <p className="text-3xl font-bold text-purple-600 mt-2">{inventory.length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-muted-foreground">Categories</p>
                <p className="text-3xl font-bold text-indigo-600 mt-2">{categories.length}</p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-muted-foreground">In Use</p>
                <p className="text-3xl font-bold text-orange-600 mt-2">
                  {inventory.reduce((sum, i) => sum + (i.quantity - i.available), 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-muted-foreground">Available</p>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {inventory.reduce((sum, i) => sum + i.available, 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Categories Management */}
      {categories.length > 0 && (
        <Card className="border-0 shadow-md">
          <CardHeader className="border-b dark:border-border bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20">
            <CardTitle className="text-lg text-gray-900 dark:text-foreground">Categories ({categories.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => {
                const itemsInCategory = inventory.filter(i => i.category === cat).length;
                return (
                  <div key={cat} className="group relative">
                    <Badge 
                      className="text-sm py-2 px-4 bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800 hover:from-purple-200 hover:to-indigo-200 dark:hover:from-purple-900/50 dark:hover:to-indigo-900/50 cursor-default"
                    >
                      {cat} ({itemsInCategory})
                      <button
                        onClick={() => handleEditCategoryClick(cat)}
                        className="ml-2 text-blue-500 hover:text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit category"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDeleteCategoryClick(cat)}
                        className="ml-1 text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete category"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredInventory.length > 0 ? (
          filteredInventory.map((item) => {
            const availabilityPercentage = getAvailabilityPercentage(item);
            return (
              <Card key={item.id} className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="border-b dark:border-border bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg text-gray-900 dark:text-foreground">{item.name}</CardTitle>
                      <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1">{item.category}</p>
                    </div>
                    <Badge className={getConditionColor(item.condition)}>
                      {item.condition}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-muted-foreground">Total Quantity:</span>
                      <span className="font-bold text-gray-900 dark:text-foreground">{item.quantity}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-muted-foreground">Available:</span>
                      <span className="font-bold text-green-600">{item.available}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-muted-foreground">In Use:</span>
                      <span className="font-bold text-orange-600">{item.quantity - item.available}</span>
                    </div>
                  </div>

                  {/* Availability Bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-gray-600 dark:text-muted-foreground">
                      <span>Availability</span>
                      <span>{availabilityPercentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                      <div 
                        className={`h-2.5 rounded-full transition-all ${
                          availabilityPercentage > 70 ? 'bg-green-500' :
                          availabilityPercentage > 40 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${availabilityPercentage}%` }}
                      />
                    </div>
                  </div>

                  {item.lastMaintenance && (
                    <div className="pt-3 border-t">
                      <p className="text-xs text-gray-600 dark:text-muted-foreground">
                        Last Maintenance: <span className="font-medium text-gray-900 dark:text-foreground">{new Date(item.lastMaintenance).toLocaleDateString()}</span>
                      </p>
                    </div>
                  )}

                  {item.notes && (
                    <div className="text-xs text-gray-600 dark:text-muted-foreground bg-gray-50 dark:bg-secondary p-3 rounded-lg">
                      {item.notes}
                    </div>
                  )}

                  <div className="flex space-x-2 pt-3">
                    <Button
                      onClick={() => handleEdit(item)}
                      variant="outline"
                      size="sm"
                      className="flex-1 hover:bg-purple-50 dark:bg-purple-900/20 hover:border-purple-300 hover:text-purple-700 transition-colors"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDeleteClick(item)}
                      variant="outline"
                      size="sm"
                      className="flex-1 hover:bg-red-50 dark:bg-red-900/20 hover:border-red-300 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="border-0 shadow-md col-span-3">
            <CardContent className="p-12 text-center">
              <Package className="w-16 h-16 text-gray-400 dark:text-muted-foreground mx-auto mb-4" />
              <p className="text-gray-600 dark:text-muted-foreground text-lg">No items found</p>
              <p className="text-gray-500 dark:text-muted-foreground text-sm mt-2">Try adjusting your filters or add a new item</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Item Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleDeleteConfirm}
        title="Delete Inventory Item?"
        description="This will permanently delete this item from your inventory. This action cannot be undone."
        itemName={itemToDelete ? `${itemToDelete.name} (${itemToDelete.category})` : ''}
        confirmText="Delete Item"
      />

      {/* Delete Category Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteCategoryConfirmOpen}
        onOpenChange={setDeleteCategoryConfirmOpen}
        onConfirm={handleDeleteCategoryConfirm}
        title="Delete Category?"
        description="This will permanently delete this category. Make sure no items are using this category."
        itemName={categoryToDelete || ''}
        confirmText="Delete Category"
      />

      {/* Edit Category Dialog */}
      <Dialog open={editCategoryDialogOpen} onOpenChange={setEditCategoryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Category
            </DialogTitle>
            <DialogDescription>
              Rename this category. All items in this category will be updated automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editCategoryName">Category Name</Label>
              <Input
                id="editCategoryName"
                value={editCategoryName}
                onChange={(e) => setEditCategoryName(e.target.value)}
                placeholder="Enter new category name"
                onKeyDown={(e) => e.key === 'Enter' && handleUpdateCategory()}
              />
            </div>
            {editingCategory && (
              <p className="text-sm text-gray-500 dark:text-muted-foreground">
                Current name: <span className="font-medium text-gray-700 dark:text-foreground">{editingCategory}</span>
              </p>
            )}
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setEditCategoryDialogOpen(false);
                setEditingCategory(null);
                setEditCategoryName('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateCategory}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventory;